import { useContext, useMemo } from 'react';
import type { Context } from 'react';
import type { Contact } from '../ContactS';
import { ContactContext } from '../Context/ContactContext';
import { ProfileContext } from '../Context/ProfileContext';
import type { Profile, ProfileShare } from '../ProfileS';
import type { useBookitupTeam } from './useBookitupTeam';

export type AssignableApplicationUser = {
  value: string;
  label: string;
  email: string;
  firstName?: string;
  lastName?: string;
};

type OwnerContact = Pick<Contact, 'email' | 'firstName' | 'lastName'>;
type AssignableProfile = Pick<Profile, 'applicationUserId'>;
type AssignableTeamMember = Pick<ProfileShare, 'applicationUserId' | 'email' | 'firstName' | 'lastName'>;

const getDisplayName = (firstName?: string, lastName?: string) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

export const getAssignableApplicationUsers = (
  profile: AssignableProfile | undefined,
  contact: OwnerContact | undefined,
  shares: AssignableTeamMember[],
): AssignableApplicationUser[] => {
  const ownerId = profile?.applicationUserId;
  const ownerEmail = contact?.email || '';
  const ownerName = getDisplayName(contact?.firstName, contact?.lastName);
  const ownerDescription = ownerName || ownerEmail;
  const owner = ownerId
    ? [
        {
          value: ownerId.toString(),
          label: ownerDescription ? `${ownerDescription} (Inhaber:in)` : 'Profilinhaber:in',
          email: ownerEmail,
          firstName: contact?.firstName,
          lastName: contact?.lastName,
        },
      ]
    : [];
  const teamMembers = shares
    .filter((share) => share.applicationUserId !== ownerId)
    .map((share) => ({
      value: share.applicationUserId.toString(),
      label: getDisplayName(share.firstName, share.lastName) || share.email,
      email: share.email,
      firstName: share.firstName,
      lastName: share.lastName,
    }));

  return [...owner, ...teamMembers];
};

export const useAssignableApplicationUsers = (
  bookitupTeamProp?: ReturnType<typeof useBookitupTeam>,
  profileProp?: Profile,
) => {
  const { profile: contextProfile, bookitupTeam } = useContext(ProfileContext);
  const contactContext = useContext(ContactContext as unknown as Context<{ contact?: OwnerContact } | undefined>);
  const profile = profileProp ?? contextProfile;
  const contact = contactContext?.contact;
  const activeBookitupTeam = bookitupTeamProp ?? bookitupTeam;

  return useMemo(
    () => getAssignableApplicationUsers(profile, contact, activeBookitupTeam.shares),
    [activeBookitupTeam.shares, contact, profile],
  );
};
